import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import type { ApiKey, ApiKeyWithSecret, Tenant } from "@repo/shared";

import { api, errorMessage } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { NewKeyDialog } from "../components/NewKeyDialog";

export function TenantDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [freshKey, setFreshKey] = useState<ApiKeyWithSecret | null>(null);
  const [keyName, setKeyName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({ name: "", contactEmail: "", rateLimitPerMinute: "" });
  const [saved, setSaved] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.api.admin.tenants[":id"].$get({ param: { id } });
      if (!res.ok) throw new Error(await errorMessage(res, "Mandant nicht gefunden"));

      const data = await res.json();
      setTenant(data.tenant);
      setApiKeys(data.apiKeys);
      setForm({
        name: data.tenant.name,
        contactEmail: data.tenant.contactEmail ?? "",
        rateLimitPerMinute: String(data.tenant.rateLimitPerMinute),
      });
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSave(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const res = await api.api.admin.tenants[":id"].$patch({
      param: { id },
      json: {
        name: form.name.trim(),
        contactEmail: form.contactEmail.trim() === "" ? null : form.contactEmail.trim(),
        rateLimitPerMinute: Number(form.rateLimitPerMinute),
      },
    });
    if (!res.ok) {
      setError(await errorMessage(res, "Speichern fehlgeschlagen"));
      return;
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    await load();
  }

  async function toggleStatus() {
    if (!tenant) return;
    const res = await api.api.admin.tenants[":id"].status.$put({
      param: { id },
      json: { status: tenant.status === "active" ? "suspended" : "active" },
    });
    if (!res.ok) {
      setError(await errorMessage(res, "Status konnte nicht geändert werden"));
      return;
    }
    await load();
  }

  async function createKey(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const res = await api.api.admin.tenants[":id"].keys.$post({
      param: { id },
      json: { name: keyName.trim() },
    });
    if (!res.ok) {
      setError(await errorMessage(res, "API-Key konnte nicht erstellt werden"));
      return;
    }
    const data = await res.json();
    setFreshKey(data.apiKey);
    setKeyName("");
    await load();
  }

  async function revokeKey(keyId: string) {
    const res = await api.api.admin.tenants[":id"].keys[":keyId"].$delete({
      param: { id, keyId },
    });
    if (!res.ok) {
      setError(await errorMessage(res, "Key konnte nicht widerrufen werden"));
      return;
    }
    await load();
  }

  async function deleteTenant() {
    const res = await api.api.admin.tenants[":id"].$delete({ param: { id } });
    if (!res.ok) {
      setError(await errorMessage(res, "Mandant konnte nicht gelöscht werden"));
      return;
    }
    navigate("/", { replace: true });
  }

  if (loading) return <p className="muted">Lade Mandant …</p>;
  if (!tenant) {
    return (
      <section>
        <p className="error">{error ?? "Mandant nicht gefunden"}</p>
        <Link to="/">Zurück zur Übersicht</Link>
      </section>
    );
  }

  return (
    <section>
      <p className="breadcrumb">
        <Link to="/">Mandanten</Link> / {tenant.name}
      </p>

      <div className="page-head">
        <h1>{tenant.name}</h1>
        <StatusBadge status={tenant.status} />
      </div>
      <p className="muted">
        Slug <code>{tenant.slug}</code> ·{" "}
        {apiKeys.filter((key) => !key.revokedAt).length} aktive Keys
      </p>

      {error && <p className="error">{error}</p>}
      {freshKey && <NewKeyDialog apiKey={freshKey} onClose={() => setFreshKey(null)} />}

      <h2>Einstellungen</h2>
      <form className="form form--stacked" onSubmit={handleSave}>
        <label className="field">
          <span>Name</span>
          <input
            required
            minLength={2}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </label>
        <label className="field">
          <span>Kontakt-E-Mail</span>
          <input
            type="email"
            value={form.contactEmail}
            onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
          />
        </label>
        <label className="field">
          <span>Rate-Limit (Anfragen/Minute)</span>
          <input
            required
            type="number"
            min={1}
            max={100000}
            value={form.rateLimitPerMinute}
            onChange={(e) => setForm({ ...form, rateLimitPerMinute: e.target.value })}
          />
        </label>
        <div className="actions">
          <button type="submit" className="primary">
            Speichern
          </button>
          {saved && <span className="muted small">Gespeichert</span>}
        </div>
      </form>

      <h2>API-Keys</h2>
      {apiKeys.length === 0 ? (
        <p className="muted">Noch kein Key für diesen Mandanten.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Bezeichnung</th>
              <th>Präfix</th>
              <th>Erstellt</th>
              <th>Zuletzt benutzt</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {apiKeys.map((key) => (
              <tr key={key.id} className={key.revokedAt ? "row--muted" : undefined}>
                <td>
                  {key.name}
                  {key.revokedAt && <span className="badge badge--suspended">widerrufen</span>}
                </td>
                <td>
                  <code>{key.prefix}…</code>
                </td>
                <td>{new Date(key.createdAt).toLocaleDateString("de-DE")}</td>
                <td>
                  {key.lastUsedAt
                    ? new Date(key.lastUsedAt).toLocaleString("de-DE")
                    : <span className="muted">nie</span>}
                </td>
                <td className="actions">
                  {!key.revokedAt && (
                    <button type="button" onClick={() => void revokeKey(key.id)}>
                      Widerrufen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <form className="form" onSubmit={createKey}>
        <input
          required
          placeholder="Bezeichnung, z. B. Kassensystem"
          value={keyName}
          onChange={(e) => setKeyName(e.target.value)}
        />
        <button type="submit" className="primary">
          API-Key erstellen
        </button>
      </form>

      <h2>Gefahrenzone</h2>
      <div className="danger-zone">
        <div className="actions">
          <button type="button" onClick={() => void toggleStatus()}>
            {tenant.status === "active" ? "Mandant sperren" : "Mandant entsperren"}
          </button>
          {confirmDelete ? (
            <>
              <button type="button" className="danger" onClick={() => void deleteTenant()}>
                Wirklich löschen
              </button>
              <button type="button" onClick={() => setConfirmDelete(false)}>
                Abbrechen
              </button>
            </>
          ) : (
            <button type="button" className="danger" onClick={() => setConfirmDelete(true)}>
              Mandant löschen
            </button>
          )}
        </div>
        <p className="muted small">
          Sperren lässt sich jederzeit zurücknehmen. Löschen entfernt Mandant, Keys und
          Produkte endgültig.
        </p>
      </div>
    </section>
  );
}
